import * as Api from '@ViewModels';
import { useMutation } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { invalidateApprovalHistory } from '../ApprovalHistory/useApprovalHistoryQuery';
import { invalidateRegEdSubmissions } from './useRegEdSubmissionsQuery';

/**
 * Supersedes the rejected round and raises a fresh one for the same approver, keeping the revision on
 * the post instead of in a new document. Rejection cancels the post *and every target*, so this
 * resets the targets as well as the status. Refuses a post that has started publishing.
 *
 * Save the revised content first — this snapshots the body as the round's reviewed content — then
 * repoint the RegEd submission and regenerate the PDF, which is what files.
 */
export const useRegEdSocialPostReviseMutation = ({
	onError,
	onSuccess,
}: {
	onError?: (error: Api.IOperationResultNoValue) => void;
	onSuccess?: (post: Api.ISocialMediaPost) => void;
} = {}) => {
	const userSession = useUserSession();
	return useMutation({
		mutationFn: ({
			postId,
			impersonationContext,
		}: {
			postId: string;
			impersonationContext?: Api.IImpersonationContext;
		}) =>
			userSession.webServiceHelper.callAsync<Api.ISocialMediaPost>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					urlPath: `social/post/${encodeURIComponent(postId)}/revise`,
				}),
				'POST',
				null
			),
		onError,
		onSuccess: (post, { postId }) => {
			invalidateRegEdSubmissions(postId);
			invalidateApprovalHistory({ resourceId: postId, resourceType: 'socialPost' });
			onSuccess?.(post);
		},
	});
};
